/**
 * studioLayerMemory — per-scenario Test-Fit Studio layer view-state.
 *
 * Layer view-state (which layers are hidden/locked, their opacity, colour
 * overrides, draw order, collapsed groups, and the soloed group) is an OPERATOR
 * PREFERENCE, not engineering truth. Like `studioViewportMemory` (camera) it
 * NEVER enters the frozen `schemaVersion:2` layout input or the replay hash —
 * geometry edits go through `commitLayoutChange`. It persists in localStorage so
 * a reload keeps the operator's layer setup, keyed by the studio `scenarioKey`
 * (`${siteId}:${scenarioId ?? 'draft'}`).
 *
 * Storage facade mirrors `siteMapLayerVisibilityMemory` — localStorage when a
 * probe round-trip succeeds, otherwise a module-level in-memory Map, so toggles
 * keep working (persistence degrades, behavior doesn't) in blocked-storage or
 * jsdom-test environments.
 */

export interface SavedStudioLayerState {
  /** Hidden layer/element ids. */
  hidden: string[];
  /** Locked ids (not selectable, not draggable). */
  locked: string[];
  /** Per-id fill opacity, 0.05–1. */
  opacity?: Record<string, number>;
  /** Per-id colour override (hex). */
  colorOverride?: Record<string, string>;
  /** Full draw order (array order = draw order, last on top). */
  order?: string[];
  /** Collapsed layer-group keys. */
  collapsed?: string[];
  /** Hidden category keys (SiteLayerCategory or a custom-category label). */
  hiddenCategories?: string[];
  /** Soloed group/layer key (everything else dimmed/hidden), or null. */
  solo?: string | null;
}

const KEY_PREFIX = 'siterag:testFitStudioLayers:';
const VERSION = 1;
const PROBE_KEY = `${KEY_PREFIX}__probe__`;

/* ── Storage facade (localStorage with in-memory fallback) ────────────── */

let memoryStore: Map<string, string> | null = null;
let probed: boolean | null = null;

function localStorageUsable(): boolean {
  if (probed !== null) return probed;
  try {
    window.localStorage.setItem(PROBE_KEY, '1');
    probed = window.localStorage.getItem(PROBE_KEY) === '1';
    window.localStorage.removeItem(PROBE_KEY);
  } catch {
    probed = false;
  }
  return probed;
}

function rawGet(key: string): string | null {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return memoryStore?.get(key) ?? null;
}

function rawSet(key: string, value: string): void {
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      /* quota — fall through to memory */
    }
  }
  (memoryStore ??= new Map()).set(key, value);
}

function rawRemove(key: string): void {
  memoryStore?.delete(key);
  if (typeof window !== 'undefined' && localStorageUsable()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* swallow */
    }
  }
}

function rawClearPrefix(prefix: string): void {
  memoryStore?.clear();
  if (typeof window === 'undefined' || !localStorageUsable()) return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(prefix)) window.localStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}

/* ── Payload validation ───────────────────────────────────────────────── */

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function numberRecord(value: unknown, min: number, max: number): Record<string, number> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function colorRecord(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v)) out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function readStudioLayerState(scenarioKey: string): SavedStudioLayerState | null {
  if (!scenarioKey) return null;
  try {
    const raw = rawGet(KEY_PREFIX + scenarioKey);
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (data.v !== VERSION) return null;
    const opacity = numberRecord(data.opacity, 0.05, 1);
    const colorOverride = colorRecord(data.colorOverride);
    const order = stringArray(data.order);
    const collapsed = stringArray(data.collapsed);
    const hiddenCategories = stringArray(data.hiddenCategories);
    const solo = typeof data.solo === 'string' ? data.solo : null;
    return {
      hidden: stringArray(data.hidden),
      locked: stringArray(data.locked),
      ...(opacity ? { opacity } : {}),
      ...(colorOverride ? { colorOverride } : {}),
      ...(order.length > 0 ? { order } : {}),
      ...(collapsed.length > 0 ? { collapsed } : {}),
      ...(hiddenCategories.length > 0 ? { hiddenCategories } : {}),
      ...(solo ? { solo } : {}),
    };
  } catch {
    return null;
  }
}

export function writeStudioLayerState(scenarioKey: string, state: SavedStudioLayerState): void {
  if (!scenarioKey) return;
  // An entirely-default state persists nothing (and clears any prior entry) so
  // the store never accumulates empty snapshots.
  const isEmpty =
    state.hidden.length === 0
    && state.locked.length === 0
    && !state.opacity
    && !state.colorOverride
    && (!state.order || state.order.length === 0)
    && (!state.collapsed || state.collapsed.length === 0)
    && (!state.hiddenCategories || state.hiddenCategories.length === 0)
    && !state.solo;
  if (isEmpty) {
    rawRemove(KEY_PREFIX + scenarioKey);
    return;
  }
  try {
    rawSet(KEY_PREFIX + scenarioKey, JSON.stringify({
      v: VERSION,
      hidden: state.hidden,
      locked: state.locked,
      ...(state.opacity && Object.keys(state.opacity).length > 0 ? { opacity: state.opacity } : {}),
      ...(state.colorOverride && Object.keys(state.colorOverride).length > 0 ? { colorOverride: state.colorOverride } : {}),
      ...(state.order && state.order.length > 0 ? { order: state.order } : {}),
      ...(state.collapsed && state.collapsed.length > 0 ? { collapsed: state.collapsed } : {}),
      ...(state.hiddenCategories && state.hiddenCategories.length > 0 ? { hiddenCategories: state.hiddenCategories } : {}),
      ...(state.solo ? { solo: state.solo } : {}),
    }));
  } catch {
    /* losing view prefs is fine */
  }
}

/** Test hook — remove every saved studio layer-state entry (both backends). */
export function clearStudioLayerStateForTests(): void {
  rawClearPrefix(KEY_PREFIX);
}
